import React, { useState, useRef, useEffect, memo } from 'react';
import { Loader2, ArrowRight } from 'lucide-react';
import './ChatInterface.css';
import './CloudEffect.css';
import Sidebar from './Sidebar';
import ContactPopup from './ContactPopup';
import ScheduleCallPopup from './ScheduleCallPopup';
import ConfirmDialog from './ConfirmDialog';
import TypewriterMarkdown from './TypewriterMarkdown';
import './TypewriterMarkdown.css';
import analyticsService from '../utils/analyticsService';

const ChatInterface = ({ 
  messages, 
  setMessages, 
  currentConversationId,
  setCurrentConversationId,
  conversations,
  onLoadConversation,
  onNewConversation,
  onRefreshConversations,
  onDeleteConversation,
  isAuthenticated,
  scrollToBottomTrigger,
  onShowSignIn,
  navigate,
  isLoadingConversation = false,
  shouldShowChat = false
}) => {
  const handleNewConversation = () => {
    onNewConversation();
    setInputMessage('');
    setStreamingMessageId(null); // Clear streaming state
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    // Track new conversation creation
    analyticsService.trackChatInteraction('start_chat');
  };

  // Handle loading conversation history
  const handleLoadConversation = (conversationId) => {
    setStreamingMessageId(null); // Clear any streaming state when loading history
    onLoadConversation(conversationId);
    // Track conversation loading
    analyticsService.trackEngagement('load_conversation', { conversation_id: conversationId });
  };

  const [isScheduleCallPopupOpen, setIsScheduleCallPopupOpen] = useState(false);

  const handleScheduleCall = () => {
    setIsScheduleCallPopupOpen(true);
    // Track schedule call button click
    analyticsService.trackButtonClick('schedule_call', 'chat_interface');
  };

  const handleCloseScheduleCallPopup = () => {
    setIsScheduleCallPopupOpen(false);
    // Close the mobile sidebar if it's open
    if (window.innerWidth <= 768) {
      // This assumes the Sidebar component has a ref or some way to access its state
      // We'll use a custom event to communicate with the Sidebar
      window.dispatchEvent(new CustomEvent('closeMobileSidebar'));
    }
  };

  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);

  const handleContact = () => {
    setIsContactPopupOpen(true);
    // Track contact button click
    analyticsService.trackButtonClick('contact', 'chat_interface');
  };

  const handleCloseContactPopup = () => {
    setIsContactPopupOpen(false);
    // Close the mobile sidebar if it's open
    if (window.innerWidth <= 768) {
      // Use the same custom event as in handleCloseScheduleCallPopup
      window.dispatchEvent(new CustomEvent('closeMobileSidebar'));
    }
  };

  // Delete confirmation handlers
  const handleRequestDelete = (conversationId, conversationTitle) => {
    setConfirmDelete({
      conversationId,
      conversationTitle
    });
  };

  const handleConfirmDelete = () => {
    if (confirmDelete) {
      onDeleteConversation(confirmDelete.conversationId);
      setConfirmDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDelete(null);
  };

  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = (behavior = 'smooth') => {
    try {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior });
      }
    } catch (error) {
      console.warn('Failed to scroll to bottom with scrollIntoView, trying fallback:', error);
      // Fallback method
      try {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      } catch (fallbackError) {
        console.warn('Fallback scroll method also failed:', fallbackError);
      }
    }
  };



  // Handle scroll trigger from parent (for conversation loading only)
  useEffect(() => {
    if (scrollToBottomTrigger) {
      setTimeout(() => {
        scrollToBottom('smooth');
      }, 300);
    }
  }, [scrollToBottomTrigger]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    // Reset textarea height when input is cleared
    if (!inputMessage && inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  }, [inputMessage]);

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  const handleInputWrapperClick = () => {
    if (!isAuthenticated && onShowSignIn) {
      // If user is not authenticated, show sign-in overlay
      onShowSignIn();
    } else if (isAuthenticated) {
      // If authenticated, focus the input as normal
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
    // Shift+Enter allows new line (default textarea behavior)
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || streamingMessageId || !isAuthenticated) return;

    // Track chat interaction
    analyticsService.trackChatInteraction('send_message', 'text');

    const userMessage = {
      id: Date.now().toString(),
      text: inputMessage,
      isUser: true,
      timestamp: new Date(),
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    const messageToSend = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    // Simple transition for first message
    if (messages.length === 0) {

    }

    try {
      // Import authenticated API function
      const { sendAuthenticatedMessage } = await import('../utils/authApi');
      
      const data = await sendAuthenticatedMessage(messageToSend, currentConversationId);
      
      const messageText = data?.message || 'I couldn\'t parse a response, but your message was received.';

      // Update conversation ID if this is a new conversation
      if (data?.conversation_id && data.conversation_id !== currentConversationId) {
        setCurrentConversationId(data.conversation_id);
        // Navigate to the new conversation URL
        if (navigate) {
          navigate(`/ch/${data.conversation_id}`, { replace: true });
        }
        // Refresh conversations list to show the new conversation
        onRefreshConversations();
      }

      const botMessageId = (Date.now() + 1).toString();
      const botMessage = {
        id: botMessageId,
        text: messageText,
        isUser: false,
        timestamp: new Date()
      };

      // Start streaming this message
      setStreamingMessageId(botMessageId);
      setMessages(prev => [...prev, botMessage]);
      
      // Track successful bot response
      analyticsService.trackChatInteraction('receive_response', 'text');
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Track error
      analyticsService.trackError('Chat message failed', 'chat_api');
      
      const errorMessageId = (Date.now() + 1).toString();
      const errorMessage = {
        id: errorMessageId,
        text: 'I\'m sorry, I encountered an error. Please try again.',
        isUser: false,
        timestamp: new Date()
      };
      
      // Start streaming this error message too
      setStreamingMessageId(errorMessageId);
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      // Refocus the input after sending message
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };



  const handleRequestNewConversation = () => {
    handleNewConversation();
    // Close the mobile sidebar if it's open
    if (window.innerWidth <= 768) {
      window.dispatchEvent(new CustomEvent('closeMobileSidebar'));
    }
  };



  const handleTypewriterComplete = (messageId) => {
    // Clear streaming state when typewriter animation is complete
    if (streamingMessageId === messageId) {
      setStreamingMessageId(null);
    }
  };

  return (
    <>
      <ContactPopup 
        isOpen={isContactPopupOpen} 
        onClose={handleCloseContactPopup} 
      />
      <ScheduleCallPopup
        isOpen={isScheduleCallPopupOpen}
        onClose={handleCloseScheduleCallPopup}
      />
      <Sidebar 
        onNewConversation={handleRequestNewConversation}
        onScheduleCall={handleScheduleCall}
        onContact={handleContact}
        isAnyPopupOpen={isScheduleCallPopupOpen || isContactPopupOpen}
        conversations={conversations}
        onLoadConversation={handleLoadConversation}
        onRequestDelete={handleRequestDelete}
        currentConversationId={currentConversationId}
        isAuthenticated={isAuthenticated}
      />
      <div className="chat-container">
      {!shouldShowChat ? (
        <div className="welcome-screen">
          <div className="welcome-content">
            <div className="talyora-title-container">
              <div className="cloud-shape cloud-animate-in"></div>
              <h1 className="talyora-title">Welcome to <span className="talyora-brand">Talyora</span></h1>
            </div>
            <h1 className="welcome-title">Talyora leverages Qaoud’s expertise to make talent acquisition simple and effective....</h1>
            <div className="welcome-input-container">
              <div 
                className="welcome-input" 
                onClick={handleInputWrapperClick}
                style={{ cursor: !isAuthenticated ? 'pointer' : 'text' }}
              >
                <textarea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={isAuthenticated ? "Ask about Qaoud's background, skills, or paste a job opportunity..." : "Please sign in to start chatting"}
                  disabled={isLoading || streamingMessageId || !isAuthenticated}
                  rows={1}
                  style={{ pointerEvents: !isAuthenticated ? 'none' : 'auto' }}
                />
                <button
                  className="send-button"
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isLoading || streamingMessageId || !isAuthenticated}
                  aria-label="Send message"
                >
                  <ArrowRight size={20} />
                </button>
              </div>

            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="chat-messages" ref={messagesContainerRef}>
            <div className="messages-container">
              {isLoadingConversation && messages.length === 0 ? (
                <div className="message bot-message">
                  <div className="message-content">
                    <div className="message-text">
                      <Loader2 className="loading-spinner" size={20} />
                    </div>
                  </div>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`message ${message.isUser ? 'user-message' : 'bot-message'}`}
                  >
                    <div className="message-content">
                      <div className="message-text">
                        {message.isUser ? (
                          // User messages appear instantly
                          message.text
                        ) : streamingMessageId === message.id ? (
                          // Only the currently streaming message uses typewriter effect
                          <TypewriterMarkdown
                            text={message.text}
                            onComplete={() => handleTypewriterComplete(message.id)}
                            className="typewriter-markdown"
                          />
                        ) : (
                          // Existing/loaded bot messages appear instantly with full markdown support
                          <TypewriterMarkdown
                            text={message.text}
                            speed={0} // Instant rendering for existing messages
                            className="typewriter-markdown message-text-static"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              {isLoading && (
                <div className="message bot-message">
                  <div className="message-content">
                    <div className="message-text">
                      <Loader2 className="loading-spinner" size={20} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="chat-input-container">
            <div 
              className="chat-input" 
              onClick={handleInputWrapperClick}
              style={{ cursor: !isAuthenticated ? 'pointer' : 'text' }}
            >
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={isAuthenticated ? "Ask about Qaoud's background, skills, or paste a job opportunity..." : "Please sign in to start chatting"}
                disabled={isLoading || streamingMessageId || !isAuthenticated}
                rows={1}
                style={{ pointerEvents: !isAuthenticated ? 'none' : 'auto' }}
              />
              <button
                className="send-button"
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading || streamingMessageId || !isAuthenticated}
                aria-label="Send message"
              >
                <ArrowRight size={20} />
              </button>
            </div>

          </div>
        </>
      )}
      </div>

      <ConfirmDialog
        isOpen={!!confirmDelete}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Delete Conversation"
        message={`Are you sure you want to delete "${confirmDelete?.conversationTitle || 'this conversation'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
};

export default memo(ChatInterface);